// ─── Types ────────────────────────────────────────────────────────────────────

export type EntitlementStatus =
  | 'active'
  | 'pending_approval'
  | 'scheduled'
  | 'expired'
  | 'revoked'
  | 'denied';

export type EntitlementOrigin =
  | 'airbnb'
  | 'hostaway'
  | 'manual_owner'
  | 'contractor'
  | 'manual_override';

export type EventType = 'granted' | 'denied' | 'revoked' | 'sync_ok' | 'sync_fail';

export interface Building {
  id: string;
  name: string;
  address: string;
  suburb: string;
  state: string;
  postcode: string;
  buildingCode: string;
  totalLots: number;
  timezone: string;
}

export interface Lot {
  lotNumber: number;
  ownerName: string;
  ownerEmail: string;
  ownerPhone: string;
  floor: string;
}

export interface Entitlement {
  id: string;
  guestName: string;
  guestEmail: string;
  guestPhone: string;
  lotNumber: number;
  ownerName: string;
  status: EntitlementStatus;
  origin: EntitlementOrigin;
  startTs: string;
  endTs: string;
  doors: string[];
  accessGroupId: string;
  createdAt: string;
  notes?: string;
}

export interface AccessEvent {
  id: string;
  eventType: EventType;
  guestName: string;
  lotNumber: number;
  door: string;
  ts: string;
  latencyMs: number;
  accessGroupId: string;
}

export interface PendingApproval {
  id: string;
  requestedBy: string;
  requestorLot: number;
  guestName: string;
  guestEmail: string;
  lotNumber: number;
  startTs: string;
  endTs: string;
  doors: string[];
  requestedAt: string;
  origin: EntitlementOrigin;
  reason: string;
}

export interface GuestTokenData {
  token: string;
  buildingName: string;
  buildingAddress: string;
  guestName: string;
  lotNumber: number;
  ownerName: string;
  startTs: string;
  endTs: string;
  doors: string[];
  enrollUrl: string;
  supportEmail: string;
  supportPhone: string;
}

// ─── Building ─────────────────────────────────────────────────────────────────

export const BUILDING: Building = {
  id: 'bld-001',
  name: 'Canopy Apartments',
  address: '12–14 Dickens Street',
  suburb: 'Elwood',
  state: 'VIC',
  postcode: '3184',
  buildingCode: 'CAN',
  totalLots: 24,
  timezone: 'Australia/Melbourne',
};

// ─── Lots ─────────────────────────────────────────────────────────────────────

export const LOTS: Lot[] = [
  {
    lotNumber: 3,
    ownerName: 'Angela Morrison',
    ownerEmail: 'angela.morrison@outlook.com.au',
    ownerPhone: '+61 412 234 567',
    floor: 'Level 1',
  },
  {
    lotNumber: 8,
    ownerName: 'Sarah Chen',
    ownerEmail: 's.chen@icloud.com',
    ownerPhone: '+61 401 987 654',
    floor: 'Level 3',
  },
  {
    lotNumber: 12,
    ownerName: 'Mike Deggs',
    ownerEmail: 'mike@thesmarterlock.com.au',
    ownerPhone: '+61 400 123 456',
    floor: 'Level 4',
  },
  {
    lotNumber: 15,
    ownerName: 'James Park',
    ownerEmail: 'jpark@gmail.com',
    ownerPhone: '+61 425 765 432',
    floor: 'Level 5',
  },
];

// ─── Helper: offset from now ──────────────────────────────────────────────────

function daysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString();
}

function daysFromNow(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString();
}

function hoursFromNow(n: number): string {
  const d = new Date();
  d.setHours(d.getHours() + n);
  return d.toISOString();
}

function minsAgo(n: number): string {
  const d = new Date();
  d.setMinutes(d.getMinutes() - n);
  return d.toISOString();
}

// ─── Entitlements ─────────────────────────────────────────────────────────────

export const INITIAL_ENTITLEMENTS: Entitlement[] = [
  {
    id: 'ent-001',
    guestName: 'James Wilson',
    guestEmail: 'jwilson@airbnb.com',
    guestPhone: '+61 499 111 222',
    lotNumber: 12,
    ownerName: 'Mike Deggs',
    status: 'active',
    origin: 'airbnb',
    startTs: daysAgo(2),
    endTs: daysFromNow(1),
    doors: ['Main Entry', 'Garage', 'Lift Lobby'],
    accessGroupId: 'AG-CAN-LOT12-GUEST',
    createdAt: daysAgo(3),
    notes: 'Airbnb booking #HBK-2284',
  },
  {
    id: 'ent-002',
    guestName: 'Emma Rodriguez',
    guestEmail: 'emma.r@buildpro.com.au',
    guestPhone: '+61 488 333 444',
    lotNumber: 8,
    ownerName: 'Sarah Chen',
    status: 'active',
    origin: 'contractor',
    startTs: hoursFromNow(-4),
    endTs: hoursFromNow(4),
    doors: ['Main Entry', 'Level 3 Corridor'],
    accessGroupId: 'AG-CAN-LOT08-CONTRACTOR',
    createdAt: daysAgo(1),
    notes: 'BuildPro maintenance — bathroom renovation',
  },
  {
    id: 'ent-003',
    guestName: 'Wei Zhang',
    guestEmail: 'wzhang@airbnb.com',
    guestPhone: '+61 477 555 666',
    lotNumber: 12,
    ownerName: 'Mike Deggs',
    status: 'scheduled',
    origin: 'airbnb',
    startTs: daysFromNow(3),
    endTs: daysFromNow(7),
    doors: ['Main Entry', 'Garage', 'Lift Lobby'],
    accessGroupId: 'AG-CAN-LOT12-GUEST',
    createdAt: daysAgo(1),
    notes: 'Airbnb booking #HBK-2291',
  },
  {
    id: 'ent-004',
    guestName: 'Priya Sharma',
    guestEmail: 'priya.s@gmail.com',
    guestPhone: '+61 466 777 888',
    lotNumber: 15,
    ownerName: 'James Park',
    status: 'pending_approval',
    origin: 'manual_owner',
    startTs: daysFromNow(5),
    endTs: daysFromNow(8),
    doors: ['Main Entry', 'Rooftop Terrace'],
    accessGroupId: 'AG-CAN-LOT15-GUEST',
    createdAt: hoursFromNow(-2),
    notes: 'Family friend visiting — rooftop access requested',
  },
  {
    id: 'ent-005',
    guestName: 'Tom Bakker',
    guestEmail: 'tombakker@hotmail.com',
    guestPhone: '+61 455 999 000',
    lotNumber: 3,
    ownerName: 'Angela Morrison',
    status: 'expired',
    origin: 'airbnb',
    startTs: daysAgo(10),
    endTs: daysAgo(7),
    doors: ['Main Entry', 'Garage'],
    accessGroupId: 'AG-CAN-LOT03-GUEST',
    createdAt: daysAgo(12),
  },
  {
    id: 'ent-006',
    guestName: 'Lisa Kowalski',
    guestEmail: 'lkowalski@airbnb.com',
    guestPhone: '+61 444 100 200',
    lotNumber: 8,
    ownerName: 'Sarah Chen',
    status: 'revoked',
    origin: 'airbnb',
    startTs: daysAgo(5),
    endTs: daysAgo(2),
    doors: ['Main Entry'],
    accessGroupId: 'AG-CAN-LOT08-GUEST',
    createdAt: daysAgo(6),
    notes: 'Early checkout — revoked on guest request',
  },
  {
    id: 'ent-007',
    guestName: 'Carlos Mendes',
    guestEmail: 'carlos.m@airbnb.com',
    guestPhone: '+61 433 300 400',
    lotNumber: 3,
    ownerName: 'Angela Morrison',
    status: 'active',
    origin: 'airbnb',
    startTs: daysAgo(1),
    endTs: daysFromNow(2),
    doors: ['Main Entry', 'Garage'],
    accessGroupId: 'AG-CAN-LOT03-GUEST',
    createdAt: daysAgo(2),
    notes: 'Airbnb booking #HBK-2276',
  },
];

// ─── Access Events ─────────────────────────────────────────────────────────────

export const INITIAL_ACCESS_EVENTS: AccessEvent[] = [
  {
    id: 'ev-001',
    eventType: 'granted',
    guestName: 'James Wilson',
    lotNumber: 12,
    door: 'Main Entry',
    ts: minsAgo(3),
    latencyMs: 312,
    accessGroupId: 'AG-CAN-LOT12-GUEST',
  },
  {
    id: 'ev-002',
    eventType: 'granted',
    guestName: 'Carlos Mendes',
    lotNumber: 3,
    door: 'Garage',
    ts: minsAgo(8),
    latencyMs: 287,
    accessGroupId: 'AG-CAN-LOT03-GUEST',
  },
  {
    id: 'ev-003',
    eventType: 'granted',
    guestName: 'Emma Rodriguez',
    lotNumber: 8,
    door: 'Main Entry',
    ts: minsAgo(22),
    latencyMs: 401,
    accessGroupId: 'AG-CAN-LOT08-CONTRACTOR',
  },
  {
    id: 'ev-004',
    eventType: 'denied',
    guestName: 'Tom Bakker',
    lotNumber: 3,
    door: 'Main Entry',
    ts: minsAgo(45),
    latencyMs: 198,
    accessGroupId: 'AG-CAN-LOT03-GUEST',
  },
  {
    id: 'ev-005',
    eventType: 'granted',
    guestName: 'James Wilson',
    lotNumber: 12,
    door: 'Lift Lobby',
    ts: minsAgo(67),
    latencyMs: 344,
    accessGroupId: 'AG-CAN-LOT12-GUEST',
  },
  {
    id: 'ev-006',
    eventType: 'granted',
    guestName: 'James Wilson',
    lotNumber: 12,
    door: 'Garage',
    ts: minsAgo(180),
    latencyMs: 267,
    accessGroupId: 'AG-CAN-LOT12-GUEST',
  },
  {
    id: 'ev-007',
    eventType: 'sync_ok',
    guestName: 'System',
    lotNumber: 0,
    door: '—',
    ts: minsAgo(15),
    latencyMs: 0,
    accessGroupId: '—',
  },
  {
    id: 'ev-008',
    eventType: 'granted',
    guestName: 'Carlos Mendes',
    lotNumber: 3,
    door: 'Main Entry',
    ts: minsAgo(240),
    latencyMs: 312,
    accessGroupId: 'AG-CAN-LOT03-GUEST',
  },
];

// ─── Pending Approvals ────────────────────────────────────────────────────────

export const INITIAL_PENDING: PendingApproval[] = [
  {
    id: 'pa-001',
    requestedBy: 'James Park',
    requestorLot: 15,
    guestName: 'Priya Sharma',
    guestEmail: 'priya.s@gmail.com',
    lotNumber: 15,
    startTs: daysFromNow(5),
    endTs: daysFromNow(8),
    doors: ['Main Entry', 'Rooftop Terrace'],
    requestedAt: hoursFromNow(-2),
    origin: 'manual_owner',
    reason: 'Family friend visiting. Rooftop access required for planned gathering on Saturday evening.',
  },
  {
    id: 'pa-002',
    requestedBy: 'Sarah Chen',
    requestorLot: 8,
    guestName: 'Daniel Cho',
    guestEmail: 'd.cho@lawfirm.com.au',
    lotNumber: 8,
    startTs: daysFromNow(2),
    endTs: daysFromNow(2),
    doors: ['Main Entry'],
    requestedAt: hoursFromNow(-5),
    origin: 'manual_owner',
    reason: 'Legal professional, one-time site visit for property settlement documents.',
  },
  {
    id: 'pa-003',
    requestedBy: 'Angela Morrison',
    requestorLot: 3,
    guestName: 'Metro Plumbing Co.',
    guestEmail: 'dispatch@metroplumbing.com.au',
    lotNumber: 3,
    startTs: daysFromNow(1),
    endTs: daysFromNow(1),
    doors: ['Main Entry', 'Garage', 'Level 1 Corridor'],
    requestedAt: hoursFromNow(-1),
    origin: 'contractor',
    reason: 'Emergency plumbing repair — hot water system replacement. Council permit #VBA-44821.',
  },
];

// ─── Guest Token (public portal demo) ─────────────────────────────────────────

export const GUEST_TOKEN_DATA: GuestTokenData = {
  token: 'demo-token-preview',
  buildingName: 'Canopy Apartments',
  buildingAddress: '12–14 Dickens Street, Elwood VIC 3184',
  guestName: 'James Wilson',
  lotNumber: 12,
  ownerName: 'Mike Deggs',
  startTs: daysAgo(2),
  endTs: daysFromNow(1),
  doors: ['Main Entry', 'Garage', 'Lift Lobby'],
  enrollUrl: 'https://credential.thesmarterlock.com.au/enrol/demo',
  supportEmail: 'support@thesmarterlock.com.au',
  supportPhone: '+61 3 9000 1234',
};

// ─── Live event generator (for admin feed) ────────────────────────────────────

const LIVE_GUESTS = ['James Wilson', 'Carlos Mendes', 'Emma Rodriguez'];
const LIVE_DOORS = ['Main Entry', 'Garage', 'Lift Lobby', 'Level 3 Corridor'];
const LIVE_LOTS = [12, 3, 8];

export function generateLiveEvent(index: number): AccessEvent {
  const guest = LIVE_GUESTS[index % LIVE_GUESTS.length];
  const door = LIVE_DOORS[index % LIVE_DOORS.length];
  const lot = LIVE_LOTS[index % LIVE_LOTS.length];
  const types: EventType[] = ['granted', 'granted', 'granted', 'denied', 'sync_ok'];
  const eventType = types[index % types.length];
  return {
    id: `ev-live-${Date.now()}-${index}`,
    eventType,
    guestName: eventType === 'sync_ok' ? 'System' : guest,
    lotNumber: eventType === 'sync_ok' ? 0 : lot,
    door: eventType === 'sync_ok' ? '—' : door,
    ts: new Date().toISOString(),
    latencyMs: eventType === 'sync_ok' ? 0 : Math.floor(Math.random() * 300 + 200),
    accessGroupId: `AG-CAN-LOT${String(lot).padStart(2, '0')}-GUEST`,
  };
}
